import {
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Headphones,
  Heart,
  ListMusic,
  Pause,
  Play,
  Printer,
  SkipBack,
  SkipForward,
  Sparkles,
  Users,
  Video,
  Volume2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import BooksHero from '../components/BooksHero';
import SafeImage from '../../../shared/components/SafeImage';
import { userService } from '../../../shared/services';
import { paymentService } from '../../../shared/services/paymentService';
import { useShop } from '../../../shared/store';
import type { ProductMediaType } from '../../../shared/types';
import { resolveAudienceFace } from '../../../shared/utils/audience';
import { sortProductsByAudienceFace } from '../../../shared/utils/bookAudience';
import { filterProductsByMediaType, getProductMediaType } from '../../../shared/utils/productMedia';
import { parseCategoryString } from '../../../shared/utils/categories';
import { convertUsdToRwf, formatRwf } from '../../../shared/utils/currency';

const faceCopy = {
  public: {
    heroBackgroundClass: 'bg-slate-100',
    badge: 'Catalog',
    titleStart: 'Discover',
    titleHighlight: 'Every Reader',
    subtitle: 'Browse a welcoming mix of kids and adult books before you sign in.',
    primaryButtonLabel: 'Login / Register',
    secondaryButtonLabel: 'Explore Library',
    spotlightTitle: 'Public Spotlight',
    spotlightBooks: [
      "Imagine We Publishers' Mission Booklet",
      "Featured Kids' Title: Bakame's Gift",
      "Featured Adult Title: The Children of a 1000 Hills - Genesis",
      'Community Reads Collection',
    ],
  },
  kids: {
    heroBackgroundClass: 'bg-orange-50',
    badge: 'Catalog',
    titleStart: 'Playful',
    titleHighlight: 'Book Adventures',
    subtitle: 'Safe, colorful, and age-appropriate stories for young readers.',
    primaryButtonLabel: 'Go to Dashboard',
    secondaryButtonLabel: 'Browse & Add',
    spotlightTitle: 'Kids Spotlight',
    spotlightBooks: [
      "Bakame's Gift",
      'Mr. Bee and Mr. Mosquito',
      'Akaleza Goes Into Space',
      'Rufigi the Little Mouse',
    ],
  },
  adults: {
    heroBackgroundClass: 'bg-slate-100',
    badge: 'Catalog',
    titleStart: 'Professional',
    titleHighlight: 'Book Picks',
    subtitle: 'Find modern novels, business reads, and insight-driven literature for 18+ readers.',
    primaryButtonLabel: 'Go to Dashboard',
    secondaryButtonLabel: 'Browse & Add',
    spotlightTitle: 'Adults Spotlight',
    spotlightBooks: [
      'Kirazira mu Rubyiruko',
      'Ubabariye, Wagira Ubuzima',
      'Business Growth in Africa',
      'Voices of the Hills',
    ],
  },
} as const;

type FaceCopy = (typeof faceCopy)['public'];

interface ProductsPageProps {
  mediaType?: ProductMediaType;
}

type AnimationHeroItem = {
  id: string;
  productId: string;
  title: string;
  genre: string;
  posterUrl: string;
  backdropUrl: string;
  match: number;
  episodes: number;
  runtime: string;
  price: number;
};

type AudioHeroTrack = {
  id: string;
  productId: string;
  title: string;
  description: string;
  coverUrl: string;
  durationLabel: string;
  price: number;
};

const animationGenres = ['Action', 'Adventure', 'Family', 'Fantasy'] as const;
const audioDurationLabels = ['3:18', '2:47', '4:05', '3:32', '5:01', '2:59'];

const getCircularOffset = (index: number, activeIndex: number, size: number): number => {
  if (size <= 1) {
    return 0;
  }

  let offset = index - activeIndex;
  const half = Math.floor(size / 2);

  if (offset > half) {
    offset -= size;
  }

  if (offset < -half) {
    offset += size;
  }

  return offset;
};

const buildAudioWaveformLevels = (count = 57): number[] => {
  const center = (count - 1) / 2;

  return Array.from({ length: count }, (_, index) => {
    const distance = Math.abs(index - center);
    const centerWeight = 1 - distance / center;
    const ripple = Math.sin((index + 1) * 1.35) * 0.5 + 0.5;
    return Number((0.1 + centerWeight * 0.86 * (0.42 + ripple * 0.58)).toFixed(3));
  });
};

const audioWaveformLevels = buildAudioWaveformLevels();
const WISHLIST_STORAGE_KEY = 'haapa.wishlist';
const numericIdPattern = /^\d+$/;
type CategoryOption = { slug: string; label: string };

interface WishlistStorageEntry {
  productId: string;
  quantity: number;
  addedAt?: string;
}

const normalizeWishlistStorageEntry = (entry: unknown): WishlistStorageEntry | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as { productId?: unknown; id?: unknown; quantity?: unknown; addedAt?: unknown };
  const rawProductId =
    (typeof record.productId === 'string' && record.productId) ||
    (typeof record.id === 'string' && record.id) ||
    '';
  const productId = rawProductId.trim();
  if (!productId) {
    return null;
  }

  const quantityValue = Number(record.quantity);
  const normalizedQuantity = Number.isFinite(quantityValue) && quantityValue > 0 ? Math.floor(quantityValue) : 1;
  const addedAt = typeof record.addedAt === 'string' ? record.addedAt : undefined;

  return {
    productId,
    quantity: normalizedQuantity,
    ...(addedAt ? { addedAt } : {}),
  };
};

const readWishlistFromStorage = (): WishlistStorageEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const raw = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    const byProductId = new Map<string, WishlistStorageEntry>();
    parsed.forEach((entry) => {
      const normalizedEntry = normalizeWishlistStorageEntry(entry);
      if (normalizedEntry) {
        byProductId.set(normalizedEntry.productId, normalizedEntry);
      }
    });
    return [...byProductId.values()];
  } catch {
    return [];
  }
};

const writeWishlistToStorage = (entries: WishlistStorageEntry[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(entries));
};

const animationFallbackHeroItems: AnimationHeroItem[] = [
  {
    id: 'fallback-animation-1',
    productId: '',
    title: 'Bakame Animated',
    genre: 'Family',
    posterUrl: '/kids/animation-story-guide.png',
    backdropUrl: '/kids/animation-story-guide.png',
    match: 97,
    episodes: 8,
    runtime: '42 min',
    price: 12.9,
  },
  {
    id: 'fallback-animation-2',
    productId: '',
    title: 'African Legends Motion',
    genre: 'Adventure',
    posterUrl: '/kids/animation-africa-story-1.png',
    backdropUrl: '/kids/animation-africa-story-1.png',
    match: 95,
    episodes: 6,
    runtime: '36 min',
    price: 11.5,
  },
  {
    id: 'fallback-animation-3',
    productId: '',
    title: 'Story Frames',
    genre: 'Fantasy',
    posterUrl: '/kids/animation-africa-story-2.png',
    backdropUrl: '/kids/animation-africa-story-2.png',
    match: 93,
    episodes: 7,
    runtime: '38 min',
    price: 13.2,
  },
  {
    id: 'fallback-animation-4',
    productId: '',
    title: 'Creative Learning Clips',
    genre: 'Action',
    posterUrl: '/kids/animation-kids-stack.png',
    backdropUrl: '/kids/animation-kids-stack.png',
    match: 92,
    episodes: 5,
    runtime: '34 min',
    price: 10.75,
  },
];

const mediaCopyOverrides: Record<ProductMediaType, Partial<FaceCopy>> = {
  book: {},
  audio: {
    badge: 'Catalog' as any,
    titleStart: 'Immersive' as any,
    titleHighlight: 'Audio Stories' as any,
    subtitle: 'Browse narrated stories and listen-ready learning content for every age.' as any,
    secondaryButtonLabel: 'Browse & Listen' as any,
    spotlightTitle: 'Audio Spotlight' as any,
    spotlightBooks: ['Bakame Audio Tales', 'Storytime Voices', 'Imigani Narrations', 'Learning by Listening'] as any,
  },
  animation: {
    badge: 'Catalog' as any,
    titleStart: 'Vibrant' as any,
    titleHighlight: 'Animated Stories' as any,
    subtitle: 'Explore visual storytelling experiences made for children, families, and lifelong learners.' as any,
    secondaryButtonLabel: 'Browse & Watch' as any,
    spotlightTitle: '' as any,
    spotlightBooks: ['Bakame Animated', 'Family Story Frames', 'African Legends Motion', 'Creative Learning Clips'] as any,
  },
};

const ProductsPage = ({ mediaType = 'book' }: ProductsPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { products, addToCart, currentUser, orders } = useShop();
  const audienceFace = resolveAudienceFace(currentUser);
  const [selectedMediaType, setSelectedMediaType] = useState<ProductMediaType>(mediaType);
  useEffect(() => {
    setSelectedMediaType(mediaType);
  }, [mediaType]);
  const currentFaceCopy = { ...faceCopy[audienceFace], ...mediaCopyOverrides[selectedMediaType] };
  const getProductDetailsPath = (productId: string): string => `/products/${productId}`;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | string>('all');
  const [displayMode, setDisplayMode] = useState<'grid' | 'compact'>('grid');
  const [quantityByProduct, setQuantityByProduct] = useState<Record<string, number>>({});
  const [wishlistEntries, setWishlistEntries] = useState<WishlistStorageEntry[]>(() => readWishlistFromStorage());
  const [vendorNamesById, setVendorNamesById] = useState<Record<string, string>>({});
  const [isPodPromoOpen, setIsPodPromoOpen] = useState(false);
  const [usdToRwfRate, setUsdToRwfRate] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rate = await paymentService.getCurrencyRate();
        if (!cancelled) setUsdToRwfRate(rate);
      } catch {
        // Conversion is optional; keep USD-only listing if config is missing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const audienceProducts = useMemo(
    () => sortProductsByAudienceFace(filterProductsByMediaType(products, selectedMediaType), audienceFace),
    [audienceFace, products, selectedMediaType],
  );

  const categories = useMemo<CategoryOption[]>(() => {
    const map = new Map<string, string>();
    audienceProducts.forEach((product) => {
      const parsed = product.categories && product.categories.length > 0
        ? product.categories
        : parseCategoryString(product.categoryKey);
      parsed.forEach((category) => {
        if (!map.has(category.slug)) {
          map.set(category.slug, category.label);
        }
      });
    });

    return [{ slug: 'all', label: 'All Categories' }, ...Array.from(map.entries()).map(([slug, label]) => ({ slug, label }))];
  }, [audienceProducts]);

  const visibleProducts = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    return audienceProducts.filter((product) => {
    const productCategories = product.categories && product.categories.length > 0
      ? product.categories
      : parseCategoryString(product.categoryKey);
    const matchesCategory =
      selectedCategory === 'all' ||
      productCategories.some((category) => category.slug === selectedCategory) ||
      product.categoryId === selectedCategory;
      const matchesSearch =
        !normalizedSearchQuery ||
        t(product.nameKey).toLowerCase().includes(normalizedSearchQuery) ||
        t(product.descriptionKey).toLowerCase().includes(normalizedSearchQuery);

      return matchesCategory && matchesSearch;
    });
  }, [audienceProducts, searchQuery, selectedCategory, t]);

  useEffect(() => {
    if (selectedCategory === 'all') {
      return;
    }

    const categoryStillValid = categories.some((category) => category.slug === selectedCategory);
    if (!categoryStillValid) {
      setSelectedCategory('all');
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    let mounted = true;

    const loadVendorNames = async () => {
      try {
        const vendors = await userService.getVendors();
        if (!mounted) {
          return;
        }

        const mappedNames: Record<string, string> = {};
        vendors.forEach((vendor) => {
          if (vendor.id && vendor.name) {
            mappedNames[vendor.id] = vendor.name;
          }
        });
        setVendorNamesById(mappedNames);
      } catch {
        if (mounted) {
          setVendorNamesById({});
        }
      }
    };

    void loadVendorNames();
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === WISHLIST_STORAGE_KEY) {
        setWishlistEntries(readWishlistFromStorage());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const isAuthenticated = Boolean(currentUser);
  const paidDigitalProductIds = useMemo(() => {
    const ownedIds = new Set<string>();

    orders.forEach((order) => {
      const normalizedPaymentStatus = (order.paymentStatus ?? '').trim().toLowerCase();
      const isPaymentConfirmed =
        normalizedPaymentStatus === 'paid' ||
        normalizedPaymentStatus === 'successful' ||
        normalizedPaymentStatus === 'succeeded' ||
        normalizedPaymentStatus === 'success' ||
        normalizedPaymentStatus === 'completed';
      const isFreeOrder = order.total <= 0;

      if (!isPaymentConfirmed && !isFreeOrder) {
        return;
      }

      order.items.forEach((item) => {
        const mediaType = item.product.mediaType ?? 'book';
        const hasDigitalAsset =
          Boolean(item.product.bookUrl) ||
          Boolean(item.product.audioUrl) ||
          Boolean(item.product.videoUrl) ||
          Boolean(item.product.contentUrl);
        const isDigitalItem =
          item.isDigital === true ||
          item.product.isDigital === true ||
          mediaType === 'audio' ||
          mediaType === 'animation' ||
          hasDigitalAsset;
        const isPrintOnly = item.isPOD === true;

        if (item.product.id && isDigitalItem && !isPrintOnly) {
          ownedIds.add(item.product.id);
        }
      });
    });

    return ownedIds;
  }, [orders]);
  const wishlistProductIds = useMemo(
    () => new Set(wishlistEntries.map((entry) => entry.productId)),
    [wishlistEntries],
  );
  const toggleWishlistItem = useCallback(
    (productId: string) => {
      if (!isAuthenticated) {
        void navigate('/auth');
        return;
      }

      setWishlistEntries((previousEntries) => {
        const exists = previousEntries.some((entry) => entry.productId === productId);
        const nextEntries = exists
          ? previousEntries.filter((entry) => entry.productId !== productId)
          : [...previousEntries, { productId, quantity: 1, addedAt: new Date().toISOString() }];
        writeWishlistToStorage(nextEntries);
        return nextEntries;
      });
    },
    [isAuthenticated, navigate],
  );
  const isBookPage = selectedMediaType === 'book';
  const isAudioPage = selectedMediaType === 'audio';
  const isAnimationPage = selectedMediaType === 'animation';

  const closePodPromo = useCallback(() => {
    setIsPodPromoOpen(false);
  }, []);

  useEffect(() => {
    if (!isBookPage) {
      setIsPodPromoOpen(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsPodPromoOpen(true);
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [isBookPage]);

  useEffect(() => {
    if (!isPodPromoOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePodPromo();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [closePodPromo, isPodPromoOpen]);

  const handlePodPromoAction = useCallback(() => {
    closePodPromo();
    void navigate('/pod');
  }, [closePodPromo, navigate]);

  const podPromoPrimaryLabel = 'Open POD page';

  const podPromoModal =
    isBookPage && isPodPromoOpen && typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 px-4 py-6 backdrop-blur-md">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(251,146,60,0.22),transparent_32%),radial-gradient(circle_at_82%_18%,rgba(245,158,11,0.18),transparent_26%),radial-gradient(circle_at_50%_100%,rgba(249,115,22,0.20),transparent_35%)]" />

            <article className="relative z-10 w-full max-w-5xl overflow-hidden rounded-[34px] border border-orange-200/20 bg-[linear-gradient(135deg,#120d0c_0%,#251411_24%,#4b230d_58%,#f7ecdd_100%)] text-white shadow-[0_45px_120px_-45px_rgba(0,0,0,0.9)]">
              <button
                type="button"
                onClick={closePodPromo}
                className="absolute right-5 top-5 z-20 rounded-full border border-white/15 bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                aria-label="Close POD promo"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="grid gap-10 px-6 py-7 sm:px-8 lg:grid-cols-[1.02fr_0.98fr] lg:px-10 lg:py-9">
                <div className="relative flex flex-col justify-center">
                  <div className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-300/25 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-200 backdrop-blur">
                    <Sparkles className="h-4 w-4 text-amber-300" />
                    New Feature
                  </div>

                  <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-orange-200/85">
                    Try our new feature
                  </p>
                  <h2 className="mt-1 text-6xl font-black uppercase leading-[0.9] text-[#ffbd73] sm:text-7xl">
                    POD
                  </h2>
                  <p className="mt-3 max-w-xl text-3xl font-semibold leading-tight text-white sm:text-4xl">
                    Turn digital stories into beautiful printed books.
                  </p>
                  <p className="mt-5 max-w-xl text-base leading-7 text-white/78">
                    Launch a print-on-demand edition with cover setup, preview mockups, pricing flow, and ready-to-publish steps all inside Haapa.
                  </p>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handlePodPromoAction}
                      className="inline-flex items-center gap-2 rounded-2xl bg-[#ff7a1a] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_-20px_rgba(249,115,22,0.75)] transition hover:-translate-y-0.5 hover:bg-[#ff8a34]"
                    >
                      <Printer className="h-4 w-4" />
                      {podPromoPrimaryLabel}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={closePodPromo}
                      className="rounded-2xl border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white/85 transition hover:bg-white/16 hover:text-white"
                    >
                      Continue browsing books
                    </button>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    {[
                      { label: 'Design', value: 'Cover + layout' },
                      { label: 'Preview', value: 'Print mockups' },
                      { label: 'Publish', value: 'Sell physical copies' },
                    ].map((item) => (
                      <div key={item.label} className="rounded-[24px] border border-white/12 bg-white/8 px-4 py-4 backdrop-blur">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-200/75">{item.label}</p>
                        <p className="mt-2 text-lg font-semibold text-white">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative flex min-h-[360px] items-center justify-center">
                  <div className="absolute h-72 w-72 rounded-full border border-amber-300/25" />
                  <div className="absolute h-[22rem] w-[22rem] rounded-full border border-orange-300/10" />
                  <div className="absolute h-64 w-64 rounded-full bg-orange-500/12 blur-3xl" />

                  <div className="relative flex h-[360px] w-full max-w-[420px] items-center justify-center">
                    {[
                      {
                        className:
                          'absolute left-2 top-24 -rotate-[16deg] bg-[linear-gradient(180deg,#0e1727_0%,#1d3557_100%)]',
                        title: 'Children Series',
                        accent: 'bg-sky-400',
                      },
                      {
                        className:
                          'absolute left-1/2 top-8 z-10 -translate-x-1/2 rotate-[-2deg] bg-[linear-gradient(180deg,#ff7a1a_0%,#8a3203_100%)]',
                        title: 'Creator Launch',
                        accent: 'bg-amber-300',
                      },
                      {
                        className:
                          'absolute right-2 top-24 rotate-[16deg] bg-[linear-gradient(180deg,#143122_0%,#246b46_100%)]',
                        title: 'Business Edition',
                        accent: 'bg-emerald-300',
                      },
                    ].map((card) => (
                      <div
                        key={card.title}
                        className={`h-[220px] w-[150px] rounded-[26px] border border-white/18 p-3 text-white shadow-[0_25px_55px_-28px_rgba(0,0,0,0.75)] ${card.className}`}
                      >
                        <div className="flex h-full flex-col rounded-[20px] border border-white/12 bg-black/10 p-3">
                          <div className={`h-1.5 w-12 rounded-full ${card.accent}`} />
                          <div className="mt-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/65">Haapa POD</p>
                            <p className="mt-2 text-2xl font-black leading-none">{card.title}</p>
                          </div>
                          <div className="mt-auto space-y-2">
                            <div className="h-20 rounded-[16px] bg-white/12" />
                            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70">
                              <span>Preview</span>
                              <span>Ready</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="relative z-20 mt-32 grid h-28 w-28 place-items-center rounded-full border border-amber-300/30 bg-[radial-gradient(circle_at_30%_30%,#ffb76a_0%,#ff8c2a_45%,#8a3203_100%)] text-white shadow-[0_25px_70px_-30px_rgba(249,115,22,0.8)]">
                      <div className="grid place-items-center rounded-full border border-white/20 bg-black/10 p-5 backdrop-blur">
                        <Printer className="h-10 w-10" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>,
          document.body,
        )
      : null;
  const animationHeroItems = useMemo<AnimationHeroItem[]>(() => {
    if (!isAnimationPage) {
      return [];
    }

    const catalogHeroItems = audienceProducts.slice(0, 14).map((product, index) => ({
      id: `${product.id}-hero`,
      productId: product.id,
      title: t(product.nameKey),
      genre: animationGenres[index % animationGenres.length],
      posterUrl: product.imageUrl,
      backdropUrl: product.imageUrl,
      match: 87 + (index % 12),
      episodes: 5 + (index % 7),
      runtime: `${30 + (index % 5) * 6} min`,
      price: Number((product.price * 1.2).toFixed(2)),
    }));

    return catalogHeroItems.length > 0 ? catalogHeroItems : animationFallbackHeroItems;
  }, [audienceProducts, isAnimationPage, t]);
  const [activeAnimationHeroId, setActiveAnimationHeroId] = useState('');

  useEffect(() => {
    if (!isAnimationPage) {
      return;
    }

    setActiveAnimationHeroId((currentId) => {
      if (animationHeroItems.some((item) => item.id === currentId)) {
        return currentId;
      }

      return animationHeroItems[0]?.id ?? '';
    });
  }, [animationHeroItems, isAnimationPage]);

  const activeAnimationHeroIndex = Math.max(
    0,
    animationHeroItems.findIndex((item) => item.id === activeAnimationHeroId),
  );
  const activeAnimationHero = animationHeroItems[activeAnimationHeroIndex] ?? animationHeroItems[0];
  const activeAnimationProduct = activeAnimationHero
    ? audienceProducts.find((product) => product.id === activeAnimationHero.productId)
    : undefined;
  const fallbackAnimationProductId = audienceProducts[0]?.id;
  const scrollToAnimationCatalog = () => {
    const catalogSection = document.getElementById('animation-product-grid');
    catalogSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shiftAnimationHero = useCallback(
    (direction: 'next' | 'prev') => {
      if (animationHeroItems.length === 0) {
        return;
      }

      setActiveAnimationHeroId((currentId) => {
        const currentIndex = animationHeroItems.findIndex((item) => item.id === currentId);
        const fallbackIndex = currentIndex === -1 ? 0 : currentIndex;
        const step = direction === 'next' ? 1 : -1;
        const nextIndex = (fallbackIndex + step + animationHeroItems.length) % animationHeroItems.length;
        return animationHeroItems[nextIndex]?.id ?? currentId;
      });
    },
    [animationHeroItems],
  );

  useEffect(() => {
    if (!isAnimationPage || animationHeroItems.length < 2) {
      return;
    }

    const autoplay = window.setInterval(() => {
      shiftAnimationHero('next');
    }, 3400);

    return () => window.clearInterval(autoplay);
  }, [animationHeroItems.length, isAnimationPage, shiftAnimationHero]);

  const audioHeroTracks = useMemo<AudioHeroTrack[]>(() => {
    if (!isAudioPage) {
      return [];
    }

    return audienceProducts.slice(0, 12).map((product, index) => ({
      id: `${product.id}-audio-hero`,
      productId: product.id,
      title: t(product.nameKey),
      description: t(product.descriptionKey),
      coverUrl: product.imageUrl,
      durationLabel: audioDurationLabels[index % audioDurationLabels.length],
      price: Number((product.price * 0.9).toFixed(2)),
    }));
  }, [audienceProducts, isAudioPage, t]);

  const [activeAudioHeroIndex, setActiveAudioHeroIndex] = useState(0);
  const [isAudioHeroPlaying, setIsAudioHeroPlaying] = useState(true);
  const [audioHeroProgress, setAudioHeroProgress] = useState(10);

  useEffect(() => {
    if (!isAudioPage) {
      return;
    }

    setActiveAudioHeroIndex((previousIndex) => (previousIndex >= audioHeroTracks.length ? 0 : previousIndex));
  }, [audioHeroTracks.length, isAudioPage]);

  useEffect(() => {
    if (!isAudioPage) {
      return;
    }

    setAudioHeroProgress(10);
  }, [activeAudioHeroIndex, isAudioPage]);

  const shiftAudioHero = useCallback(
    (direction: 'next' | 'prev') => {
      if (audioHeroTracks.length === 0) {
        return;
      }

      setActiveAudioHeroIndex((previousIndex) =>
        direction === 'next'
          ? (previousIndex + 1) % audioHeroTracks.length
          : (previousIndex - 1 + audioHeroTracks.length) % audioHeroTracks.length,
      );
    },
    [audioHeroTracks.length],
  );

  useEffect(() => {
    if (!isAudioPage || !isAudioHeroPlaying || audioHeroTracks.length < 2) {
      return;
    }

    const autoplay = window.setInterval(() => {
      shiftAudioHero('next');
    }, 3600);

    return () => window.clearInterval(autoplay);
  }, [audioHeroTracks.length, isAudioHeroPlaying, isAudioPage, shiftAudioHero]);

  useEffect(() => {
    if (!isAudioPage || !isAudioHeroPlaying || audioHeroTracks.length === 0) {
      return;
    }

    const progressTimer = window.setInterval(() => {
      setAudioHeroProgress((previousProgress) => Math.min(100, previousProgress + 3));
    }, 120);

    return () => window.clearInterval(progressTimer);
  }, [audioHeroTracks.length, isAudioHeroPlaying, isAudioPage]);

  const activeAudioHeroTrack = audioHeroTracks[activeAudioHeroIndex] ?? audioHeroTracks[0];

  return (
    <section className="space-y-6">
      {podPromoModal}

      {/* HERO */}
      {isAnimationPage && activeAnimationHero ? (
        <div className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 overflow-hidden pb-2 pt-0 sm:pb-3">
          <article
            className="relative overflow-hidden bg-slate-950 text-white"
            style={{
              minHeight: 'clamp(520px, calc(100svh - 180px), 760px)',
              backgroundImage: `linear-gradient(180deg, rgba(5,5,8,0.08) 45%, rgba(5,5,8,0.75) 80%, rgba(5,5,8,0.96) 100%), linear-gradient(100deg, rgba(5,5,8,0.95) 14%, rgba(5,5,8,0.35) 56%, rgba(5,5,8,0.9) 100%), url('${activeAnimationHero.backdropUrl}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 15% 20%, rgba(249,115,22,0.24), transparent 44%), radial-gradient(circle at 82% 14%, rgba(251,146,60,0.15), transparent 34%)',
              }}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent via-black/45 to-black/95" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-8 pt-14 sm:px-8 sm:pb-10 sm:pt-16 lg:px-12 lg:pb-52 lg:pt-24">
              <div className="max-w-2xl space-y-5 text-white">
                <p className="inline-flex items-center rounded-full border border-white/35 bg-white/10 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-orange-200 backdrop-blur">
                  {currentFaceCopy.badge}
                </p>

                <h2 className="text-4xl font-bold leading-tight sm:text-6xl lg:text-7xl">
                  {activeAnimationHero.title}
                </h2>
                <p className="max-w-xl text-sm text-white/85 sm:text-base">{currentFaceCopy.subtitle}</p>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const productIdToOpen = activeAnimationHero.productId || fallbackAnimationProductId;
                      if (!productIdToOpen) {
                        return;
                      }

                      const selectedProduct = audienceProducts.find((product) => product.id === productIdToOpen);
                      if (selectedProduct && Number(selectedProduct.price) > 0) {
                        void navigate(getProductDetailsPath(productIdToOpen));
                        return;
                      }
                      void navigate(getProductDetailsPath(productIdToOpen));
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] text-white transition hover:bg-orange-600"
                  >
                    <Play className="h-4 w-4" />
                    Watch
                  </button>

                  {activeAnimationHero.price > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (activeAnimationProduct) {
                          void navigate(getProductDetailsPath(activeAnimationHero.productId));
                          return;
                        }
                        scrollToAnimationCatalog();
                      }}
                      className="rounded-full border border-white/70 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] text-orange-700 transition hover:bg-orange-50"
                    >
                      Buy RWF {activeAnimationHero.price.toFixed(2)}
                    </button>
                  )}
                </div>

              </div>
            </div>
            <div className="relative z-20 mt-10 bg-transparent px-4 py-4 sm:px-6 lg:absolute lg:inset-x-0 lg:bottom-0 lg:mt-0">
              <div className="mx-auto w-full max-w-7xl">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-300">
                    {currentFaceCopy.spotlightTitle}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => shiftAnimationHero('prev')}
                      className="rounded-full border border-white/30 bg-white/10 p-1.5 text-white transition hover:border-orange-300 hover:bg-white/20"
                      aria-label="Slide left"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => shiftAnimationHero('next')}
                      className="rounded-full border border-white/30 bg-white/10 p-1.5 text-white transition hover:border-orange-300 hover:bg-white/20"
                      aria-label="Slide right"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {animationHeroItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveAnimationHeroId(item.id)}
                      className={`shrink-0 overflow-hidden rounded-md border transition ${
                        item.id === activeAnimationHero.id
                          ? 'border-orange-400 ring-2 ring-orange-300/70'
                          : 'border-white/25 hover:border-orange-300'
                      }`}
                    >
                      <div className="relative">
                        <SafeImage
                          src={item.posterUrl}
                          alt={item.title}
                          className="h-28 w-24 object-cover sm:h-36 sm:w-28 lg:h-40 lg:w-32"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
                          <p className="line-clamp-1 text-[10px] font-semibold text-white">{item.title}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      ) : isBookPage ? (
        <BooksHero />
      ) : isAudioPage && activeAudioHeroTrack ? (
        <div className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 overflow-hidden py-2 sm:py-4">
          <article className="relative overflow-hidden bg-gradient-to-br from-[#8b4308] via-[#c86f13] to-[#f4aa43] text-white">
            <style>{`
              @keyframes audio-wave-pulse {
                0%, 100% { transform: scaleY(0.28); }
                50% { transform: scaleY(1); }
              }
            `}</style>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 18% 15%, rgba(255,224,160,0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(255,142,65,0.3), transparent 38%), radial-gradient(circle at 50% 100%, rgba(120,28,8,0.48), transparent 55%)',
              }}
            />
            <div
              className="absolute inset-0 opacity-25 blur-sm"
              style={{ backgroundImage: `url('${activeAudioHeroTrack.coverUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-0 bg-[#3d1b07]/38" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-12 pt-16 sm:px-6 sm:pt-20 lg:px-8">
              <article className="mx-auto max-w-4xl space-y-3 text-center text-white">
                <p className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur">
                  <Headphones className="h-3.5 w-3.5" />
                  {currentFaceCopy.badge}
                </p>
                <h2 className="text-4xl font-semibold sm:text-6xl">Listen to African Stories</h2>
                <p className="mx-auto max-w-3xl text-sm text-white/85 sm:text-base">{currentFaceCopy.subtitle}</p>
              </article>

              <div className="mx-auto mt-9 h-[320px] w-full max-w-5xl [perspective:1200px]">
                {audioHeroTracks.map((track, index) => {
                  const offset = getCircularOffset(index, activeAudioHeroIndex, audioHeroTracks.length);
                  const absoluteOffset = Math.abs(offset);
                  if (absoluteOffset > 2) {
                    return null;
                  }

                  const shift = `calc(${offset} * min(20vw, 170px))`;
                  const rise = absoluteOffset === 0 ? 0 : absoluteOffset * 18;
                  const rotation = offset * -8;
                  const scale = absoluteOffset === 0 ? 1 : 0.9 - absoluteOffset * 0.08;
                  const opacity = absoluteOffset === 2 ? 0.44 : absoluteOffset === 1 ? 0.78 : 1;
                  const isActiveCard = absoluteOffset === 0;

                  return (
                    <button
                      key={track.id}
                      type="button"
                      onClick={() => {
                        setActiveAudioHeroIndex(index);
                        setIsAudioHeroPlaying(true);
                      }}
                      className="absolute left-1/2 top-0 w-[210px] transform-gpu transition duration-700 sm:w-[250px]"
                      style={{
                        transform: `translateX(calc(-50% + ${shift})) translateY(${rise}px) rotate(${rotation}deg) scale(${scale})`,
                        zIndex: 40 - absoluteOffset,
                        opacity,
                      }}
                    >
                      <article
                        className={`overflow-hidden rounded-[26px] border shadow-[0_28px_80px_-35px_rgba(0,0,0,0.85)] backdrop-blur ${
                          isActiveCard ? 'border-white/70 bg-white/18' : 'border-white/35 bg-white/10'
                        }`}
                      >
                        <SafeImage src={track.coverUrl} alt={track.title} className="h-52 w-full object-cover sm:h-64" />
                        <div className="space-y-0.5 bg-gradient-to-b from-transparent to-black/45 px-3 py-3 text-center text-white sm:px-4">
                          <p className="line-clamp-1 text-xl font-semibold leading-tight">{track.title}</p>
                          <p className="line-clamp-1 text-sm text-white/85">{track.description}</p>
                        </div>
                      </article>
                    </button>
                  );
                })}
              </div>

              <div className="mx-auto mt-4 w-full max-w-5xl rounded-[999px] border border-white/35 bg-[#2a1207]/58 p-2.5 text-white backdrop-blur-xl sm:p-3.5">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => shiftAudioHero('prev')}
                      className="rounded-full p-2 text-white/85 transition hover:bg-white/15 hover:text-white"
                      aria-label="Skip previous"
                    >
                      <SkipBack className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAudioHeroPlaying((playing) => !playing)}
                      className="rounded-full bg-white/20 p-2.5 text-white transition hover:bg-white/30"
                      aria-label={isAudioHeroPlaying ? 'Pause' : 'Play'}
                    >
                      {isAudioHeroPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => shiftAudioHero('next')}
                      className="rounded-full p-2 text-white/85 transition hover:bg-white/15 hover:text-white"
                      aria-label="Skip next"
                    >
                      <SkipForward className="h-4 w-4" />
                    </button>
                  </div>

                  <article className="min-w-[220px] flex-1 rounded-2xl border border-white/15 bg-black/35 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <SafeImage
                        src={activeAudioHeroTrack.coverUrl}
                        alt={activeAudioHeroTrack.title}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{activeAudioHeroTrack.title}</p>
                        <p className="truncate text-xs text-white/75">{activeAudioHeroTrack.description}</p>
                      </div>
                      <p className="ml-auto text-[11px] font-semibold text-white/80">{activeAudioHeroTrack.durationLabel}</p>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/20">
                      <div className="h-full rounded-full bg-orange-300 transition-all duration-300" style={{ width: `${audioHeroProgress}%` }} />
                    </div>
                    <div className="mt-2 flex h-8 items-center justify-between gap-[3px] overflow-hidden px-0.5">
                      {audioWaveformLevels.map((level, index) => (
                        <span
                          key={`${activeAudioHeroTrack.id}-wave-${index}`}
                          className="w-[3px] rounded-full bg-white/90 transition-opacity duration-300"
                          style={{
                            height: `${Math.max(2, Math.round(4 + level * 24))}px`,
                            opacity: isAudioHeroPlaying ? 0.95 : 0.45,
                            animation: isAudioHeroPlaying
                              ? `audio-wave-pulse ${0.78 + (index % 7) * 0.08}s ease-in-out ${index * 0.03}s infinite`
                              : 'none',
                            transformOrigin: 'center',
                          }}
                        />
                      ))}
                    </div>
                  </article>

                  <div className="ml-auto flex items-center gap-1 sm:gap-2">
                    <button
                      type="button"
                      className="rounded-full p-2 text-white/85 transition hover:bg-white/15 hover:text-white"
                      aria-label="Audio devices"
                    >
                      <Headphones className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="rounded-full p-2 text-white/85 transition hover:bg-white/15 hover:text-white"
                      aria-label="Track list"
                    >
                      <ListMusic className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="rounded-full p-2 text-white/85 transition hover:bg-white/15 hover:text-white"
                      aria-label="Volume"
                    >
                      <Volume2 className="h-4 w-4" />
                    </button>
                    {activeAudioHeroTrack.price > 0 && (
                      <button
                        type="button"
                        onClick={() => void navigate(getProductDetailsPath(activeAudioHeroTrack.productId))}
                        className="rounded-full bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600"
                      >
                        Buy RWF {activeAudioHeroTrack.price.toFixed(2)}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mx-auto mt-4 flex max-w-5xl items-center justify-center gap-2">
                {audioHeroTracks.slice(0, 7).map((track, index) => (
                  <button
                    key={`${track.id}-dot`}
                    type="button"
                    onClick={() => setActiveAudioHeroIndex(index)}
                    className={`h-1.5 rounded-full transition ${
                      index === activeAudioHeroIndex ? 'w-10 bg-white/95' : 'w-5 bg-white/35 hover:bg-white/55'
                    }`}
                    aria-label={`Select ${track.title}`}
                  />
                ))}
              </div>
            </div>
          </article>
        </div>
      ) : (
        <div className={`relative -mx-4 overflow-hidden px-4 py-16 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 ${currentFaceCopy.heroBackgroundClass}`}>
          <div className="absolute left-8 top-20 h-12 w-12 rounded-full bg-orange-300/40" />
          <div className="absolute right-10 top-14 h-16 w-16 rounded-full bg-orange-200/60" />
          <div className="absolute bottom-8 left-1/3 h-14 w-14 rounded-full bg-orange-300/30" />

          <div className="mx-auto max-w-4xl text-center">
            <span className="inline-flex rounded-full border border-orange-200 bg-white px-4 py-1 text-xs font-semibold tracking-wide text-orange-600">
              {currentFaceCopy.badge}
            </span>

            <h2 className="mt-5 text-4xl font-bold text-slate-800 md:text-6xl">
              {currentFaceCopy.titleStart}{' '}
              <span className="text-orange-600">
                {currentFaceCopy.titleHighlight}
              </span>
            </h2>

            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
              {currentFaceCopy.subtitle}
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to={!currentUser ? '/auth' : currentUser.role === 'admin' ? '/admin/dashboard/overview' : '/dashboard/home'}
                className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
              >
                {currentFaceCopy.primaryButtonLabel}
              </Link>

              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated) {
                    void navigate('/auth');
                    return;
                  }
                  void navigate('/checkout');
                }}
                className="rounded-xl border border-orange-300 bg-white px-5 py-3 text-sm font-semibold text-orange-600 transition hover:bg-orange-50"
              >
                {currentFaceCopy.secondaryButtonLabel}
              </button>
            </div>

            <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-white/90 px-4 py-3 text-xs font-semibold text-slate-700">
              {currentFaceCopy.spotlightTitle && (
                <span className="uppercase tracking-wide text-orange-600">{currentFaceCopy.spotlightTitle}:</span>
              )}
              {currentFaceCopy.spotlightBooks.map((book) => (
                <span key={`${audienceFace}-${book}`} className="rounded-full bg-orange-50 px-3 py-1 text-[11px] text-orange-700">
                  {book}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FORMAT FILTER + SEARCH */}
      <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex gap-2 rounded-full bg-orange-50 p-1">
            {(
              [
                { type: 'book', label: 'Written' },
                { type: 'audio', label: 'Audio' },
                { type: 'animation', label: 'Animation' },
              ] as { type: ProductMediaType; label: string }[]
            ).map(({ type, label }) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedMediaType(type)}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition ${
                  selectedMediaType === type
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'bg-white text-orange-700 hover:bg-orange-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('products.searchPlaceholder')}
            className="w-full rounded-full border border-slate-200 bg-slate-50 px-5 py-3 text-sm text-slate-700 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
          />

        </div>
      </div>

      {/* CATEGORY + VIEW MODE */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const isActive = selectedCategory === category.slug;
            return (
              <button
                key={category.slug}
                type="button"
                onClick={() => setSelectedCategory(category.slug)}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition ${
                  isActive
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                }`}
              >
                {category.slug === 'all' ? t('products.allCategories') : category.label}
              </button>
            );
          })}
        </div>

        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          <button
            type="button"
            onClick={() => setDisplayMode('grid')}
            className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
              displayMode === 'grid'
                ? 'bg-orange-100 text-orange-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('products.gridLabel')}
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode('compact')}
            className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
              displayMode === 'compact'
                ? 'bg-orange-100 text-orange-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('products.compactLabel')}
          </button>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div
        id="animation-product-grid"
        className={`grid gap-5 ${
          displayMode === 'grid'
            ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'sm:grid-cols-2 lg:grid-cols-3'
        }`}
      >
        {visibleProducts.map((product, index) => {
          const quantity = Math.max(1, quantityByProduct[product.id] ?? 1);
          const productMediaType = getProductMediaType(product);
          const isFree = product.price <= 0;
          const isOwnedDigital = paidDigitalProductIds.has(product.id);
          const originalPrice = product.price * 1.25;
          const mediaLabel =
            productMediaType === 'book' ? 'Book' : productMediaType === 'audio' ? 'Audio' : 'Animation';
          const actionLabel =
            productMediaType === 'animation' ? 'Watch' : productMediaType === 'audio' ? 'Listen' : 'Read';
          const creatorRawValue = (product.authorName || product.author || '').trim();
          const creatorName = creatorRawValue
            ? vendorNamesById[creatorRawValue] ||
              (numericIdPattern.test(creatorRawValue) ? 'Creator' : creatorRawValue)
            : '';
          const creatorPrefix = productMediaType === 'book' ? 'Written by' : 'Uploaded by';
          const actionIcon =
            productMediaType === 'book'
              ? <BookOpen className="h-4 w-4" />
              : productMediaType === 'audio'
                ? <Headphones className="h-4 w-4" />
                : <Video className="h-4 w-4" />;
          const categoryChips = product.categories && product.categories.length > 0
            ? product.categories
            : parseCategoryString(product.categoryKey);

          return (
            <article
              key={product.id}
              className="animate-fade-up overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_12px_32px_-24px_rgba(249,115,22,0.55)]"
              style={{ animationDelay: `${index * 55}ms` }}
            >
              {/*
               Free animation items open in playlist mode.
               Paid animation items open the media details page (locked until purchase).
              */}
              {(() => {
                const animationWatchTarget = getProductDetailsPath(product.id);
                return (
                  <>
              <div className="relative">
                <Link to={animationWatchTarget}>
                  <SafeImage
                    src={product.imageUrl}
                    alt={t(product.nameKey)}
                    className={`w-full object-cover transition duration-500 hover:scale-105 ${
                      displayMode === 'grid' ? 'h-44' : 'h-36'
                    }`}
                  />
                </Link>
                <div className="absolute right-3 top-3 grid gap-2">
                  {(() => {
                    const isWishlisted = wishlistProductIds.has(product.id);
                    return (
                  <button
                    type="button"
                    onClick={() => toggleWishlistItem(product.id)}
                    className="rounded-lg border border-white/70 bg-white/90 p-1.5 text-slate-700 shadow-sm transition hover:bg-white"
                    aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                    title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                  >
                    <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-orange-500 text-orange-500' : ''}`} />
                  </button>
                    );
                  })()}
                </div>
              </div>

              <div className="space-y-2 p-4">
                <span className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  {mediaLabel}
                </span>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {t(product.nameKey)}
                  </h3>
                  {creatorName && (
                    <p className="text-xs font-semibold text-orange-600">
                      {creatorPrefix} {creatorName}
                    </p>
                  )}
                </div>

                <p className="line-clamp-2 text-sm text-slate-600">
                  {t(product.descriptionKey)}
                </p>

                {categoryChips.length > 0 && (
                  <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
                    {categoryChips.map((category) => (
                      <span
                        key={`${product.id}-${category.slug}`}
                        className="rounded-full border border-orange-100 bg-orange-50 px-2.5 py-1 text-orange-700"
                      >
                        {category.label}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  {isFree ? (
                    <p className="text-lg font-bold text-orange-500">Free</p>
                  ) : (
                    <>
                      <div>
                        <p className="text-lg font-bold text-orange-500">$ {product.price.toFixed(2)}</p>
                        {usdToRwfRate && usdToRwfRate > 0 && (
                          <p className="text-[11px] font-semibold text-slate-500">
                            (approx. {formatRwf(convertUsdToRwf(product.price, usdToRwfRate))})
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 line-through">$ {originalPrice.toFixed(2)}</p>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3">
                  {isOwnedDigital ? (
                    <Link
                      to={productMediaType === 'book' ? `/products/${product.id}/pageview` : getProductDetailsPath(product.id)}
                      className="block w-full rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-center text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        {actionIcon}
                        {productMediaType === 'book' ? 'Read' : actionLabel}
                      </span>
                    </Link>
                  ) : isFree ? (
                    <Link
                      to={animationWatchTarget}
                      className="block w-full rounded-lg border border-slate-200 px-4 py-2 text-center text-sm font-semibold text-slate-600 transition hover:border-orange-300 hover:text-orange-600"
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        {actionIcon}
                        {`${actionLabel} now`}
                      </span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (!isAuthenticated) {
                          void navigate('/auth');
                          return;
                        }
                        addToCart(product, 1);
                      }}
                      className="block w-full rounded-lg bg-orange-500 px-4 py-2 text-center text-sm font-semibold text-white transition hover:bg-orange-600"
                    >
                      {t('products.addToCart')}
                    </button>
                  )}
                </div>
              </div>
                  </>
                );
              })()}
            </article>
          );
        })}
      </div>

      {visibleProducts.length === 0 && (
        <div className="rounded-2xl border border-orange-100 bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-700">{t('products.emptyState')}</p>
        </div>
      )}
    </section>
  );
};

export default ProductsPage;
